import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createRol } from "@/api/usuarios";
import { TextField } from "@/components/ui/TextField";
import { Button } from "@/components/ui/Button";
import { showToast } from "@/components/ui/Toast";
import { useFocusTrap } from "@/hooks/useFocusTrap";

interface AgregarRolModalProps {
  open: boolean;
  onClose: () => void;
}

export const AgregarRolModal = ({ open, onClose }: AgregarRolModalProps) => {
  const queryClient = useQueryClient();
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");

  const create = useMutation({
    mutationFn: () =>
      createRol({
        nombre: nombre.trim(),
        descripcion: descripcion.trim() || undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["roles"] });
      showToast("success", "Rol creado exitosamente");
      handleClose();
    },
    onError: (err: Error) => {
      showToast("error", "No se pudo crear el rol", err.message);
    },
  });

  const handleClose = () => {
    setNombre("");
    setDescripcion("");
    onClose();
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!nombre.trim()) {
      showToast("warning", "El nombre es obligatorio");
      return;
    }
    create.mutate();
  };

  const dialogRef = useFocusTrap(open, { onEscape: handleClose });

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="agregar-rol-title"
      onClick={handleClose}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-elevated"
        onClick={(event) => event.stopPropagation()}
      >
        <h2
          id="agregar-rol-title"
          className="text-lg font-semibold text-ink-primary"
        >
          Agregar Nuevo Rol
        </h2>
        <p className="mt-1 text-sm text-ink-secondary">
          Define el nombre y la descripción para el nuevo rol de usuario.
        </p>

        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          <TextField
            label="Nombre del Rol"
            name="nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Ej. Coordinador"
            required
            autoFocus
          />

          <TextField
            as="textarea"
            label="Descripción"
            name="descripcion"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Describe las responsabilidades de este rol..."
            rows={3}
          />

          <div className="mt-6 flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={create.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={create.isPending}>
              Crear Rol
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};
